import * as THREE from 'three'
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js'
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js'

export interface STLLoadResult {
  geometry: THREE.BufferGeometry
  mesh: THREE.Mesh
  // Optional backend file id associated with this STL payload.
  fileId?: string
  metadata: {
    vertices: number
    faces: number
    boundingBox: THREE.Box3
    size: THREE.Vector3
    center: THREE.Vector3
  }
  scaleFactor?: number // Scale factor applied during normalization (scene units / original units)
}

export class GirasolesSTLLoader {
  public loader: STLLoader
  
  constructor() {
    this.loader = new STLLoader()
  }

  /**
   * Load STL file from URL and return processed geometry
   */
  async loadSTLFromURL(url: string): Promise<STLLoadResult> {
    return new Promise((resolve, reject) => {
      this.loader.load(
        url,
        (geometry) => {
          try {
            const result = this.processGeometry(geometry)
            resolve(result)
          } catch (error) {
            reject(new Error(`Failed to process STL geometry: ${error}`))
          }
        },
        (progress) => {
          console.log('STL loading progress:', (progress.loaded / progress.total) * 100 + '%')
        },
        (error) => {
          reject(new Error(`Failed to load STL file: ${error}`))
        }
      )
    })
  }

  /**
   * Load STL file from File object (for drag & drop or file input)
   */
  async loadSTLFromFile(file: File): Promise<STLLoadResult> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = (event) => {
        try {
          const arrayBuffer = event.target?.result as ArrayBuffer
          const geometry = this.loader.parse(arrayBuffer)
          const result = this.processGeometry(geometry)
          resolve(result)
        } catch (error) {
          reject(new Error(`Failed to parse STL file: ${error}`))
        }
      }

      reader.onerror = () => {
        reject(new Error('Failed to read STL file'))
      }

      reader.readAsArrayBuffer(file)
    })
  }

  /**
   * Load STL file from ArrayBuffer (for backend downloads)
   */
  async loadSTLFromArrayBuffer(arrayBuffer: ArrayBuffer): Promise<STLLoadResult> {
    try {
      const geometry = this.loader.parse(arrayBuffer)

      // DEBUG: Log the raw geometry dimensions right after parsing
      geometry.computeBoundingBox()
      const rawBox = geometry.boundingBox!
      const rawSize = new THREE.Vector3()
      rawBox.getSize(rawSize)
      console.log('🔍 [STLLoader] RAW geometry dimensions after parsing (before any processing):')
      console.log('🔍 [STLLoader] Raw size:', { x: rawSize.x, y: rawSize.y, z: rawSize.z })
      console.log('🔍 [STLLoader] Raw max dimension:', Math.max(rawSize.x, rawSize.y, rawSize.z))

      const result = this.processGeometry(geometry)
      return result
    } catch (error) {
      throw new Error(`Failed to parse STL from ArrayBuffer: ${error}`)
    }
  }

  /**
   * Process loaded geometry and create mesh with metadata
   */
  processGeometry(inputGeometry: THREE.BufferGeometry): STLLoadResult {
    // STL often carries per-face normals that make surfaces look faceted.
    // Merge vertices and always recompute vertex normals for smoother shading.
    const geometry = inputGeometry.index ? inputGeometry : mergeVertices(inputGeometry, 1e-5)
    if (geometry !== inputGeometry) {
      inputGeometry.dispose()
    }
    geometry.computeVertexNormals()
    geometry.normalizeNormals()

    // Compute bounding box and center
    geometry.computeBoundingBox()
    const boundingBox = geometry.boundingBox!
    const size = new THREE.Vector3()
    const center = new THREE.Vector3()
    
    boundingBox.getSize(size)
    boundingBox.getCenter(center)

    // Create material for the mesh
    const material = new THREE.MeshPhongMaterial({
      color: 0x8B4513, // Brown color for foot/insole
      shininess: 30,
      transparent: true,
      opacity: 0.9
    })

    // Create mesh
    const mesh = new THREE.Mesh(geometry, material)
    mesh.castShadow = true
    mesh.receiveShadow = true

    // Calculate metadata
    const vertices = geometry.attributes.position.count
    const faces = vertices / 3 // Assuming triangular faces

    return {
      geometry,
      mesh,
      metadata: {
        vertices,
        faces,
        boundingBox,
        size,
        center
      }
    }
  }

  /**
   * Create a wireframe version of the mesh
   */
  createWireframe(geometry: THREE.BufferGeometry): THREE.LineSegments {
    const wireframeGeometry = new THREE.WireframeGeometry(geometry)
    const wireframeMaterial = new THREE.LineBasicMaterial({ 
      color: 0x000000,
      transparent: true,
      opacity: 0.3
    })
    
    return new THREE.LineSegments(wireframeGeometry, wireframeMaterial)
  }

  /**
   * Center and scale geometry to fit in a standard view
   * Returns the scale factor applied (scene units / original units)
   */
  normalizeGeometry(geometry: THREE.BufferGeometry, targetSize: number = 10): number {
    geometry.computeBoundingBox()
    const boundingBox = geometry.boundingBox!
    const size = new THREE.Vector3()
    const center = new THREE.Vector3()

    boundingBox.getSize(size)
    boundingBox.getCenter(center)

    // Calculate scale factor
    const maxDimension = Math.max(size.x, size.y, size.z)
    const scale = targetSize / maxDimension

    // DEBUG: Log the original dimensions before scaling
    console.log('🔧 [normalizeGeometry] BEFORE scaling:')
    console.log('🔧 [normalizeGeometry] Original size:', { x: size.x, y: size.y, z: size.z })
    console.log('🔧 [normalizeGeometry] Max dimension:', maxDimension)
    console.log('🔧 [normalizeGeometry] Target size:', targetSize)
    console.log('🔧 [normalizeGeometry] Calculated scale factor:', scale)

    // Center and scale the geometry
    geometry.translate(-center.x, -center.y, -center.z)
    geometry.scale(scale, scale, scale)

    // Recompute bounding box after transformation
    geometry.computeBoundingBox()

    // Return the scale factor so measurements can be corrected
    return scale
  }
}

export function exportSTLString(geometry: THREE.BufferGeometry, normalizationScaleFactor: number = 1): string {
  // STL writer needs a non-indexed geometry where triangle vertices are laid out sequentially.
  // Some of our processing (e.g. `mergeVertices`) can produce indexed BufferGeometry, and
  // exporting without flattening would scramble faces on re-load.
  let exportGeometry = geometry.clone()
  if (exportGeometry.index) {
    exportGeometry = exportGeometry.toNonIndexed()
  }
  if (!exportGeometry.attributes.normal) {
    exportGeometry.computeVertexNormals()
  }

  if (Number.isFinite(normalizationScaleFactor) && normalizationScaleFactor > 0 && normalizationScaleFactor !== 1) {
    const inverseScale = 1 / normalizationScaleFactor
    exportGeometry.scale(inverseScale, inverseScale, inverseScale)
    exportGeometry.computeVertexNormals()
  }

  const vertices = exportGeometry.attributes.position.array
  let stlString = 'solid girasoles\n'
  for (let i = 0; i < vertices.length; i += 9) {
    stlString += `  facet normal 0 0 0\n    outer loop\n      vertex ${vertices[i]} ${vertices[i + 1]} ${vertices[i + 2]}\n      vertex ${vertices[i + 3]} ${vertices[i + 4]} ${vertices[i + 5]}\n      vertex ${vertices[i + 6]} ${vertices[i + 7]} ${vertices[i + 8]}\n    endloop\n  endfacet\n`
  }
  exportGeometry.dispose()
  return `${stlString}endsolid girasoles\n`
}

// Export singleton instance
export const stlLoader = new GirasolesSTLLoader()

// STL file paths for the application
export const STL_FILES = {
  LUIS_ABREU_R: '/assets/stl/luis_abreu_R.stl',
  // Add more STL files here as needed
} as const

export type STLFileKey = keyof typeof STL_FILES
